/**
 * Canvas utilities for graph rendering
 */

/**
 * Get the pixel ratio for the current device.
 */
export function getPixelRatio() {
  return window.devicePixelRatio || 1;
}

/**
 * Clear the canvas.
 */
export function clearCanvas(context, width, height) {
  context.clearRect(0, 0, width, height);
}

/**
 * Create a DOM canvas element.
 */
export function createCanvas(width, height, pixelRatio = 1) {
  const canvas = document.createElement("canvas");
  canvas.setAttribute("width", width * pixelRatio + "px");
  canvas.setAttribute("height", height * pixelRatio + "px");
  canvas.style.width = width + "px";
  canvas.style.height = height + "px";
  return canvas;
}

/**
 * Get dimensions of the canvas.
 */
export function getCanvasDimensions(canvas) {
  return {
    width: canvas.width,
    height: canvas.height
  };
}

/**
 * Handle high DPI screens.
 */
export function setCanvasPixelRatio(canvas, context) {
  const pixelRatio = getPixelRatio();
  if (pixelRatio !== 1) {
    const width = canvas.width;
    const height = canvas.height;
    canvas.width = width * pixelRatio;
    canvas.height = height * pixelRatio;
    canvas.style.width = width + "px";
    canvas.style.height = height + "px";
    context.scale(pixelRatio, pixelRatio);
  }
  return pixelRatio;
}

/**
 * Create a canvas buffer.
 */
export function createCanvasBuffer(width, height) {
  const canvas = createCanvas(width, height);
  const context = canvas.getContext("2d");
  return { canvas, context };
}

/**
 * Draw a label on the canvas.
 */
export function drawLabel(context, x, y, label, settings = {}) {
  const {
    font = "12px sans-serif",
    fillStyle = "#000",
    strokeStyle = "#fff",
    lineWidth = 2,
    textAlign = "center",
    textBaseline = "middle"
  } = settings;

  context.font = font;
  context.textAlign = textAlign;
  context.textBaseline = textBaseline;

  // Draw stroke for better visibility on different backgrounds
  if (lineWidth > 0) {
    context.strokeStyle = strokeStyle;
    context.lineWidth = lineWidth;
    context.lineJoin = "round";
    context.strokeText(label, x, y);
  }

  // Draw text
  context.fillStyle = fillStyle;
  context.fillText(label, x, y);
}

/**
 * Draw a circle on the canvas.
 */
export function drawCircle(context, x, y, radius, settings = {}) {
  const {
    fillStyle = "#000",
    strokeStyle = null,
    lineWidth = 0
  } = settings;

  context.beginPath();
  context.arc(x, y, radius, 0, Math.PI * 2);
  context.closePath();

  if (fillStyle) {
    context.fillStyle = fillStyle;
    context.fill();
  }

  if (strokeStyle && lineWidth > 0) {
    context.strokeStyle = strokeStyle;
    context.lineWidth = lineWidth;
    context.stroke();
  }
}

/**
 * Draw a rectangle on the canvas.
 */
export function drawRectangle(context, x, y, width, height, settings = {}) {
  const {
    fillStyle = "#000",
    strokeStyle = null,
    lineWidth = 0
  } = settings;

  if (fillStyle) {
    context.fillStyle = fillStyle;
    context.fillRect(x, y, width, height);
  }

  if (strokeStyle && lineWidth > 0) {
    context.strokeStyle = strokeStyle;
    context.lineWidth = lineWidth;
    context.strokeRect(x, y, width, height);
  }
}

/**
 * Draw a line on the canvas.
 */
export function drawLine(context, x1, y1, x2, y2, settings = {}) {
  const {
    strokeStyle = "#000",
    lineWidth = 1,
    lineDash = []
  } = settings;

  context.beginPath();
  context.moveTo(x1, y1);
  context.lineTo(x2, y2);
  context.strokeStyle = strokeStyle;
  context.lineWidth = lineWidth;
  
  if (lineDash.length > 0) {
    context.setLineDash(lineDash);
  }
  
  context.stroke();
  
  if (lineDash.length > 0) {
    context.setLineDash([]);
  }
}

/**
 * Helper function to wrap text to a maximum width
 */
function wrapText(context, text, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const width = context.measureText(currentLine + " " + words[i]).width;
    if (width < maxWidth) {
      currentLine += " " + words[i];
    } else {
      lines.push(currentLine);
      currentLine = words[i];
    }
  }
  lines.push(currentLine);
  return lines;
}

/**
 * Draw a hover effect around a node with labels
 */
export function drawHover(context, data, settings = {}) {
  const {
    labelSize = 12,
    labelFont = "sans-serif",
    labelWeight = "normal",
    color = "#000",
    backgroundColor = "#fff",
    shadowColor = "rgba(0, 0, 0, 0.2)",
    shadowBlur = 8,
    shadowOffsetX = 0,
    shadowOffsetY = 2,
    padding = 8
  } = settings;

  const subLabelSize = labelSize - 2;
  const label = data.label || "";
  const subLabel = data.tag !== "unknown" ? data.tag : "";
  const clusterLabel = data.clusterLabel || "";
  const description = data.description || "";
  const MAX_LINE_WIDTH = 300;

  // Draw the label background
  context.beginPath();
  context.fillStyle = backgroundColor;
  context.shadowOffsetX = shadowOffsetX;
  context.shadowOffsetY = shadowOffsetY;
  context.shadowBlur = shadowBlur;
  context.shadowColor = shadowColor;

  // Measure text widths
  context.font = `${labelWeight} ${labelSize}px ${labelFont}`;
  const labelWidth = context.measureText(label).width;
  context.font = `${labelWeight} ${subLabelSize}px ${labelFont}`;
  const subLabelWidth = subLabel ? context.measureText(subLabel).width : 0;
  const clusterLabelWidth = clusterLabel ? context.measureText(clusterLabel).width : 0;
  
  // Calculate description width with wrapping
  let descriptionWidth = 0;
  let descriptionLines = [];
  if (description) {
    descriptionLines = wrapText(context, description, MAX_LINE_WIDTH);
    descriptionWidth = Math.max(...descriptionLines.map(line => context.measureText(line).width));
  }

  const textWidth = Math.max(labelWidth, subLabelWidth, clusterLabelWidth, descriptionWidth);
  const totalWidth = Math.round(textWidth + padding * 2);
  const totalHeight = Math.round(
    (label ? labelSize + padding : 0) +
    (subLabel ? subLabelSize + padding/2 : 0) +
    (clusterLabel ? subLabelSize + padding/2 : 0) +
    (description ? (subLabelSize + padding/2 + 3) * descriptionLines.length : 0)
  );

  const x = Math.round(data.x + data.size + 4); // Offset from node
  const y = Math.round(data.y - totalHeight/2); // Center vertically

  // Draw rounded rectangle background
  context.beginPath();
  context.moveTo(x + 5, y);
  context.lineTo(x + totalWidth - 5, y);
  context.quadraticCurveTo(x + totalWidth, y, x + totalWidth, y + 5);
  context.lineTo(x + totalWidth, y + totalHeight - 5);
  context.quadraticCurveTo(x + totalWidth, y + totalHeight, x + totalWidth - 5, y + totalHeight);
  context.lineTo(x + 5, y + totalHeight);
  context.quadraticCurveTo(x, y + totalHeight, x, y + totalHeight - 5);
  context.lineTo(x, y + 5);
  context.quadraticCurveTo(x, y, x + 5, y);
  context.closePath();
  context.fill();

  // Reset shadow
  context.shadowOffsetX = 0;
  context.shadowOffsetY = 0;
  context.shadowBlur = 0;

  // Draw labels
  let currentY = y + padding;
  context.fillStyle = color;
  
  if (label) {
    context.font = `${labelWeight} ${labelSize}px ${labelFont}`;
    context.fillText(label, x + padding, currentY + labelSize);
    currentY += labelSize + padding/2;
  }

  if (subLabel) {
    context.font = `${labelWeight} ${subLabelSize}px ${labelFont}`;
    context.fillText(subLabel, x + padding, currentY + subLabelSize);
    currentY += subLabelSize + padding/2;
  }

  if (clusterLabel) {
    context.font = `${labelWeight} ${subLabelSize}px ${labelFont}`;
    context.fillStyle = data.color || color;
    context.fillText(clusterLabel, x + padding, currentY + subLabelSize);
    currentY += subLabelSize + padding/2;
  }

  if (description) {
    context.font = `${labelWeight} ${subLabelSize}px ${labelFont}`;
    context.fillStyle = color;
    descriptionLines.forEach((line, index) => {
      context.fillText(line, x + padding, currentY + subLabelSize);
      currentY += subLabelSize + padding/2;
    });
  }
} 